import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { Add, CheckCircle, Cancel } from "@mui/icons-material";
import { settingsAPI } from "../../services/api";

const RepositorySettings = ({ configurationId }) => {
  const [repositories, setRepositories] = useState([]);
  const [repositoryType, setRepositoryType] = useState("GitHub");
  const [repositoryUrl, setRepositoryUrl] = useState("");
  const [personalAccessToken, setPersonalAccessToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadRepositories = useCallback(async () => {
    if (!configurationId) {
      setRepositories([]);
      return;
    }
    try {
      setError("");
      // Ensure configurationId is a number
      const configId =
        typeof configurationId === "string"
          ? parseInt(configurationId, 10)
          : configurationId;
      console.log(
        "RepositorySettings: Loading repositories for configurationId:",
        configId,
        "Type:",
        typeof configId,
      );
      const response = await settingsAPI.getRepositories(configId);
      console.log("RepositorySettings: Received repositories:", response.data);
      setRepositories(response.data || []);
    } catch (err) {
      console.error("Failed to load repositories:", err);
      setError(err.response?.data?.error || "Failed to load repositories.");
      setRepositories([]);
    }
  }, [configurationId]);

  useEffect(() => {
    // Clear previous state when configuration changes
    console.log(
      "RepositorySettings: Configuration changed to:",
      configurationId,
    );
    setError("");
    setSuccess("");
    setRepositories([]); // Clear immediately when configuration changes

    if (configurationId) {
      loadRepositories();
    } else {
      setRepositories([]);
    }
  }, [configurationId, loadRepositories]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!configurationId) {
      setError("Please select a configuration first");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await settingsAPI.createRepository({
        repository_type: repositoryType,
        repository_url: repositoryUrl,
        personal_access_token: personalAccessToken,
        configuration_id: configurationId,
      });
      setSuccess("Repository connection verified and saved successfully!");
      setRepositoryUrl("");
      setPersonalAccessToken("");
      await loadRepositories();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to connect repository");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Repository Connections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect to Azure Git, Bitbucket, or GitHub repositories. The application
        will verify the connection and PAT token permissions before saving.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Repository Type</InputLabel>
          <Select
            value={repositoryType}
            onChange={(e) => setRepositoryType(e.target.value)}
            label="Repository Type"
          >
            <MenuItem value="Azure Git">Azure Git</MenuItem>
            <MenuItem value="GitHub">GitHub</MenuItem>
            <MenuItem value="Bitbucket">Bitbucket</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Repository URL"
          variant="outlined"
          margin="normal"
          value={repositoryUrl}
          onChange={(e) => setRepositoryUrl(e.target.value)}
          required
          placeholder="https://github.com/username/repo.git"
        />
        <TextField
          fullWidth
          label="Personal Access Token (PAT)"
          type="password"
          variant="outlined"
          margin="normal"
          value={personalAccessToken}
          onChange={(e) => setPersonalAccessToken(e.target.value)}
          required
          helperText="The PAT will be verified before saving"
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<Add />}
          sx={{ mt: 2 }}
          disabled={loading}
        >
          {loading ? "Verifying..." : "Connect Repository"}
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Verified At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repositories.map((repo) => (
              <TableRow key={repo.id}>
                <TableCell>{repo.repository_type}</TableCell>
                <TableCell>{repo.repository_url}</TableCell>
                <TableCell>
                  {repo.is_verified ? (
                    <Chip
                      icon={<CheckCircle />}
                      label="Verified"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<Cancel />}
                      label="Not Verified"
                      color="error"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {repo.verified_at
                    ? new Date(repo.verified_at).toLocaleDateString()
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RepositorySettings;
